/**
 * Recording Recovery Modal Component
 * Shows when interrupted recordings are detected
 * Allows user to resume or discard interrupted recordings
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  MicrophoneIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { RecordingSession } from '@/stores/recordingSessionStore';

interface RecordingRecoveryModalProps {
  isOpen: boolean;
  session: RecordingSession;
  onResume: () => void;
  onDiscard: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

export const RecordingRecoveryModal: React.FC<RecordingRecoveryModalProps> = ({
  isOpen,
  session,
  onResume,
  onDiscard,
  onClose,
  isLoading = false,
}) => {
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInterruptionReasonText = (reason?: RecordingSession['interruptionReason']): string => {
    switch (reason) {
      case 'app_background':
        return 'App went to background';
      case 'phone_call':
        return 'Phone call interrupted';
      case 'network_error':
        return 'Network connection lost';
      case 'user_paused':
        return 'Recording was paused';
      default:
        return 'Recording was interrupted';
    }
  };

  const handleDiscard = () => {
    if (showDiscardConfirm) {
      onDiscard();
      setShowDiscardConfirm(false);
    } else {
      setShowDiscardConfirm(true);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header with warning icon */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 border-b border-amber-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Interrupted Recording Found
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  {getInterruptionReasonText(session.interruptionReason)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="ml-auto text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Recording details */}
          <div className="p-6 space-y-4">
            {/* Patient info if available */}
            {session.patientName && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {session.patientName}
                  </p>
                  <p className="text-xs text-slate-500">
                    Patient
                  </p>
                </div>
              </div>
            )}

            {/* Recording stats */}
            <div className="grid grid-cols-2 gap-3">
              {/* Duration */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                <ClockIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {formatDuration(session.totalDuration)}
                  </p>
                  <p className="text-xs text-slate-500">Duration</p>
                </div>
              </div>

              {/* Segments saved */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {session.segments.length} segments
                  </p>
                  <p className="text-xs text-slate-500">Saved</p>
                </div>
              </div>
            </div>

            {/* Timestamp */}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <MicrophoneIcon className="w-4 h-4" />
              <span>Started {formatDate(session.startedAt)}</span>
              {session.lastActiveAt !== session.startedAt && (
                <>
                  <span>•</span>
                  <span>Last active {formatDate(session.lastActiveAt)}</span>
                </>
              )}
            </div>

            {/* Info message */}
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-blue-700">
                Your recording was automatically saved. You can resume where you left off
                or discard it if no longer needed.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 pt-0 space-y-3">
            {/* Discard confirmation */}
            <AnimatePresence>
              {showDiscardConfirm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 rounded-xl p-4 mb-3"
                >
                  <p className="text-sm text-red-700 mb-3">
                    Are you sure? This will permanently delete the recorded audio.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDiscardConfirm(false)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDiscard}
                      disabled={isLoading}
                      className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
                    >
                      {isLoading ? 'Deleting...' : 'Delete Forever'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main buttons */}
            {!showDiscardConfirm && (
              <div className="flex gap-3">
                <button
                  onClick={handleDiscard}
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Discard</span>
                </button>
                <button
                  onClick={onResume}
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-shadow disabled:opacity-50"
                >
                  <PlayIcon className="w-4 h-4" />
                  <span>{isLoading ? 'Loading...' : 'Resume'}</span>
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Interrupted Recording Banner
 * Compact banner shown on Dashboard when interrupted recordings exist
 */
interface InterruptedRecordingBannerProps {
  sessionCount: number;
  onReview: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const InterruptedRecordingBanner: React.FC<InterruptedRecordingBannerProps> = ({
  sessionCount,
  onReview,
  onDismiss,
  className = '',
}) => {
  if (sessionCount === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-amber-50 border border-amber-200 rounded-xl p-4 ${className}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-amber-800">
            Interrupted Recording{sessionCount > 1 ? 's' : ''} Detected
          </p>
          <p className="text-sm text-amber-600">
            You have {sessionCount} recording{sessionCount > 1 ? 's' : ''} that {sessionCount > 1 ? 'were' : 'was'} interrupted.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-2 text-amber-500 hover:text-amber-700 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onReview}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors"
          >
            Review
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default RecordingRecoveryModal;
